/**
 * Settings → Watching → Telegram (SPEC `New Post Watching` § Settings modal, §5.1 #73-#76).
 *
 * A **second** bot binding, not a second use of the first: a separate chat is what lets a "someone
 * just posted" buzz sound different from — or be muted independently of — a viral alert. The
 * mechanics are the viral tab's, verbatim, against `settings.telegramWatch`: three faces the server
 * picks (handshake → bound → how-to), a 2 s poll while a code is outstanding, and every sentence
 * about a failure is the server's (§12.5).
 *
 * The one thing this face adds is the SPEC's quiet unbound hint: no bot is not a broken state, it
 * just means detections stay in the app.
 */

import { useState } from 'react';
import type { TelegramStateResponse } from '@lw/shared';
import { describeError } from '../../api/client';
import { ConfirmDialog, ErrorState, Spinner } from '../../components';
import {
  useConnectWatchTelegram,
  useDisconnectWatchTelegram,
  useTestWatchTelegram,
  useWatchTelegramQuery,
} from './useWatch';

const BOTFATHER_URL = 'https://t.me/BotFather';

/** `details: [{key, message}]` from a #74 `400`, flattened for inline rendering (§11.1). */
function fieldErrorsOf(error: unknown): Record<string, string> {
  const details = (error as { details?: unknown } | null)?.details;
  if (!Array.isArray(details)) return {};
  const errors: Record<string, string> = {};
  for (const entry of details) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { key, message } = entry as { key?: unknown; message?: unknown };
    if (typeof key === 'string' && typeof message === 'string') errors[key] = message;
  }
  return errors;
}

export function WatchTelegramSection() {
  const query = useWatchTelegramQuery();
  const state = query.data;

  if (query.isPending) return <Spinner size="lg" label="Loading the watch bot…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load the watch bot settings"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (state === undefined) return null;

  if (state.pending !== null) return <PendingSection state={state} />;
  if (state.connected) return <ConnectedSection state={state} />;
  return <DisconnectedSection />;
}

/* ------------------------------------------------------------ 1 · disconnected */

function DisconnectedSection() {
  const connect = useConnectWatchTelegram();
  const [token, setToken] = useState('');
  const errors = fieldErrorsOf(connect.error);

  return (
    <section className="settings-section">
      <h3 className="settings-title">Telegram</h3>
      <p className="field__hint">
        Connect a Telegram bot to get pushed — detections still appear in the app.
      </p>
      <ol className="settings-steps">
        <li>
          Open{' '}
          <a href={BOTFATHER_URL} target="_blank" rel="noreferrer noopener">
            @BotFather
          </a>{' '}
          in Telegram and send <code>/newbot</code>.
        </li>
        <li>Pick a name and a username for it — a different bot from your viral alerts.</li>
        <li>Paste the token it gives you here.</li>
      </ol>

      <div className="field">
        <label className="field__label" htmlFor="watch-telegram-token">
          Bot token
        </label>
        <input
          id="watch-telegram-token"
          className="input"
          type="password"
          autoComplete="off"
          placeholder="123456789:AA…"
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
          }}
        />
        {errors.token !== undefined ? (
          <span className="settings-bad">{errors.token}</span>
        ) : (
          <span className="field__hint">
            A second bot means a second chat: mute one without muting the other.
          </span>
        )}
      </div>

      <div className="row settings-actions">
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            connect.mutate(token.trim(), {
              onSuccess: () => {
                setToken('');
              },
            });
          }}
          disabled={token.trim().length === 0 || connect.isPending}
        >
          {connect.isPending ? <Spinner size="sm" /> : null}
          Connect
        </button>
      </div>

      {connect.isError && Object.keys(errors).length === 0 ? (
        <p className="form-error" role="alert">
          {describeError(connect.error)}
        </p>
      ) : null}
    </section>
  );
}

/* ---------------------------------------------------------------- 2 · pending */

function PendingSection({ state }: { state: TelegramStateResponse }) {
  const cancel = useDisconnectWatchTelegram();
  const pending = state.pending;
  if (pending === null) return null;

  return (
    <section className="settings-section">
      <h3 className="settings-title">
        Finish in Telegram{state.bot === null ? '' : ` — ${state.bot.name}`}
      </h3>
      <p className="field__hint">
        Open the chat with the bot and press <strong>Start</strong>. The first private chat that
        sends this code is the one new-post alerts go to.
      </p>

      <div className="row settings-actions">
        <a className="btn-primary" href={pending.deepLink} target="_blank" rel="noreferrer noopener">
          Open {state.bot === null ? 'the bot' : `@${state.bot.username}`} in Telegram
        </a>
      </div>

      <div className="field">
        <span className="field__label">Or send this code to the bot</span>
        <span className="telegram-code">{pending.code}</span>
      </div>

      <div className="row settings-actions" role="status">
        <Spinner size="sm" />
        <span className="muted">Waiting for the code…</span>
        <button
          type="button"
          className="btn-ghost btn-ghost--sm"
          onClick={() => {
            cancel.mutate();
          }}
          disabled={cancel.isPending}
        >
          Cancel
        </button>
      </div>

      {cancel.isError ? (
        <p className="form-error" role="alert">
          {describeError(cancel.error)}
        </p>
      ) : null}
    </section>
  );
}

/* -------------------------------------------------------------- 3 · connected */

function ConnectedSection({ state }: { state: TelegramStateResponse }) {
  const test = useTestWatchTelegram();
  const disconnect = useDisconnectWatchTelegram();
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      {state.error !== null ? (
        <p className="settings-warn" role="alert">
          {state.error.message}
        </p>
      ) : null}

      <section className="settings-section">
        <h3 className="settings-title">Telegram</h3>
        <dl className="settings-readonly">
          <div>
            <dt>Bot</dt>
            <dd>
              {state.bot === null ? '—' : state.bot.name}
              {state.bot === null ? null : <span className="faint"> @{state.bot.username}</span>}
            </dd>
          </div>
          <div>
            <dt>Chat</dt>
            <dd>{state.chat?.label ?? '—'}</dd>
          </div>
          <div>
            <dt>Connected</dt>
            <dd>{state.connectedAtDisplay ?? '—'}</dd>
          </div>
          <div>
            <dt>Last message sent</dt>
            <dd>{state.lastSentAtDisplay ?? 'Nothing sent yet'}</dd>
          </div>
        </dl>

        <div className="row settings-actions">
          <button
            type="button"
            className="table-action"
            onClick={() => {
              test.mutate();
            }}
            disabled={test.isPending}
          >
            {test.isPending ? <Spinner size="sm" /> : null}
            <span className="table-action__label">Send test message</span>
          </button>
          {test.isSuccess ? <span className="settings-ok">Sent.</span> : null}
          {test.isError ? <span className="settings-bad">{describeError(test.error)}</span> : null}
          <button
            type="button"
            className="btn-ghost btn-ghost--sm"
            onClick={() => {
              setConfirming(true);
            }}
            disabled={disconnect.isPending}
          >
            Disconnect
          </button>
        </div>

        {disconnect.isError ? (
          <p className="form-error" role="alert">
            {describeError(disconnect.error)}
          </p>
        ) : null}
      </section>

      <ConfirmDialog
        open={confirming}
        title="Disconnect the watch bot?"
        message="New-post pushes will stop arriving in Telegram; queued ones are dropped. Detections still appear in the app."
        confirmLabel="Disconnect"
        destructive
        busy={disconnect.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          disconnect.mutate(undefined, {
            onSettled: () => {
              setConfirming(false);
            },
          });
        }}
      />
    </>
  );
}

export default WatchTelegramSection;
